/*
 * moda-coping-pdp.js — calculator A: the "by length" coping calculator on the product page (plan §2, §3, §4).
 * Needs, in order: moda-coping-takeoff.js (engine, untouched copy of v3), moda-coping-estimate.js, then this file.
 * Never loads the PDF code.
 *
 * Markup contract (the theme section writes this; the preview page copies it):
 *   <div class="mcp" data-moda-coping-calc [data-color="White"] [data-config-id="moda-coping-config"]>
 *     …fallback for when scripts don't run: a request-a-quote link and support@modaconcrete.com…
 *   </div>
 *   <script type="application/json" id="moda-coping-config">{ variants, money_format, colors, pieces, images, urls, copy… }</script>
 * The script replaces the fallback with the calculator. The config shape is documented in preview/config.json.
 * Prices and variant ids come only from that config (Shopify variants); this file holds none.
 *
 * Layout (Eugene 2026-10-05: "PDP look more like what we created for Pavers mixed with how we were inputting by length …
 * all 3 pieces images and adjustable − + … and under area for Overage"), top to bottom:
 *   color row · "Coping calculator" + size badge · total length (ft + in, tape fractions) · "Your pieces": three ledger
 *   rows like the configurator's (56 px photo in the chosen color, name "Straight" / "Inside corner" / "Outside corner"
 *   from config pieces[k].title, spec line with the corner's config note, "$90.00 each") with a quantity field
 *   (− count +) on each · Overage None · 5 % · 10 % · Sealer (the paver page's $2 / sq ft penetrating sealer, a checkbox;
 *   only when the config has addons.sealer) · estimated total · Add to cart (a muted line under it says why while it's
 *   off) · "Made to order" · Open the configurator (the freeform-pool quote line went 2026-10-14, Eugene).
 *
 * Behaviour
 *   - Straights are estimated: ModaCopingEstimate.estimate({ lengthIn, outside, inside, sparePct }) (quick() + 1 per
 *     edge, overage after). − / + or typing a count makes it the customer's own (sticky: the length, corners and overage
 *     no longer change it); "Set by you · Reset to N" puts the estimate back. With no length the straights are 0 and can
 *     still be set by hand. Straights stop at 400: typing more shows 400 and "More than 400? Request a quote…".
 *   - Inside corners (default 4, 0–50) and outside corners (default 0, 0–30) are the customer's counts and feed the
 *     estimate. When inside ≠ outside + 4 a quiet note says a full pool has four more inside corners than outside ones.
 *   - Ready to add when the estimate stands (a length that reads) or, with no length, the customer changed any count
 *     themselves (V.hand: a few corners is an order); a length that can't be read or is shorter than the corners needs
 *     fixing first.
 *   - Mode is automatic: a needed piece whose variant is unavailable or unpriced → quote mode (no prices, no total,
 *     "Request a quote"). A length past the calculator's limit is quote mode too, and so is an estimate of more than
 *     400 straights (a 960 ft typo for 96 ft): every count the page can put in the cart stays at or under 400.
 *   - Sealer (Eugene 2026-10-13: "we charge $2 for applying extra sealer like we have on 24 x 24 pages"): off by
 *     default, as on the paver page. Its quantity is the top face of the pieces shown (spares too), whole sq ft for
 *     each piece line (ModaCopingEstimate.sealer), so it follows every count; its price shows once there's an order.
 *     Chosen and unpriced or unavailable, it's quote mode like a piece.
 *   - Add to cart: the live paver calculator's addItems() (sections, cart:change, /cart fallback), one line per piece
 *     with a count (exactly the counts shown), properties "Pool opening" (when a length was entered), "Corners", and
 *     hidden _plan (the configurator #t= link) and _v = 4. With the sealer, the paver page's add-on convention, which
 *     the live theme's mc-cart-addons.js reads as one product line and its add-on lines per _group: each piece line
 *     gets its own _group and "Add-ons: Penetrating sealer", followed by its sealer line (quantity = that piece's sq ft)
 *     with "Applies to" and "Coverage". No sealer, no _group (a _group line without add-ons gets its quantity locked
 *     and an empty "Add-ons" label in the live cart).
 *   - "Open the configurator" is a real <a target="moda-coping"> whose href is rewritten on every input to
 *     urls.configurator + "#t=" + the v3-compatible state: with a length, shape "lf" with lf = { in, out, inside }; with
 *     none, the configurator's default pool, lf still carrying both corner counts; allowance = the overage either way.
 *     The configurator opens a drawing made from them and says so. When this tab is shown again after it was opened,
 *     the header cart bubble is re-rendered through the Section Rendering API (?sections=cart-icon-bubble).
 *   - Inputs are kept per tab in sessionStorage ("moda-coping-calc": color, ft, inch, inside, outside, spare, sealer,
 *     straight (null = estimated), hand; an older entry with outside only gets inside = 4 + outside); a #t= hash on the product
 *     URL wins over that and starts from the estimate, and is then taken off the URL (history.replaceState), so a reload
 *     keeps the customer's edits.
 *   - Fires "moda-coping:change" on the root (bubbles) with { color, estimate, counts, manual, mode, state, href }.
 */
(function () {
  'use strict';
  const STORE = 'moda-coping-calc';
  // 400 straights is about 760 ft of coping: past that a typo is likelier than a pool, and a quote plans it (QA 2026-10-05)
  const MAX = { straight: 400, inside: 50, outside: 30 }; // the configurator draws up to 30 outside corners
  const SPARES = [0, 5, 10];
  const TXT = {
    lenHint: 'Add up every side of the pool, wall to wall.',
    invalid: 'Enter the length in feet and inches, like 96 ft 6 1/2 in.',
    short: "That's shorter than your corner pieces. Check the measurement, or ask us about spas.",
    long: "That's longer than this calculator plans. Request a quote and we'll plan it with you.",
    mismatch: 'A full pool has four more inside corners than outside ones. Fine for part of a pool.',
    sEmpty: 'Enter your length to estimate',
    sAuto: 'From your length',
    sCheck: 'Check the length above',
    sLong: "We'll plan this length with your quote",
    sMan: 'Set by you',
    overage: 'Extra straights for cuts and breakage.',
    overageMan: 'Extra straights for cuts and breakage. Your own straight count stays as you set it.',
    capBefore: `More than ${MAX.straight}? `,
    capAfter: " and we'll plan it with you.",
    addHint: 'Enter your length, or set your pieces, to add to cart.',
    addCheck: 'Check the length above to add to cart.',
    quote: 'Pricing comes with your quote.',
    pieceQuote: 'Priced with your quote',
    sealName: 'Penetrating sealer',
    sealNote: 'Applied at our plant before shipping. Guards against stains and moisture.',
    sealQuote: 'Choosing it moves your order to a quote',
    added: 'Added to your cart.',
    addFail: "We couldn't add this to your cart."
  };
  let seq = 0;

  const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  // a count: a whole number 0–max, or null when it isn't one
  const count = (v, max) => { const n = typeof v === 'string' ? (/^\s*\d+\s*$/.test(v) ? +v : NaN) : Number(v); return Number.isInteger(n) && n >= 0 ? Math.min(max, n) : null; };
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const store = {
    get() { try { return JSON.parse(sessionStorage.getItem(STORE) || 'null'); } catch (e) { return null; } },
    set(v) { try { sessionStorage.setItem(STORE, JSON.stringify(v)); } catch (e) { /* private mode */ } }
  };

  // ── cart: copied from the live modapave-coverage.js (theme t/59) ──
  function getCart() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function cartSectionIds(cart) {
    if (cart && typeof cart.getSectionsToRender === 'function') {
      return cart.getSectionsToRender().map(function (s) { return s.id; }).join(',');
    }
    return 'cart-drawer,cart-icon-bubble';
  }

  function addItems(items) {
    var cart = getCart();
    var body = { items: items };
    if (cart) {
      body.sections = cartSectionIds(cart);
      body.sections_url = window.location.pathname;
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (d) {
        if (!r.ok || d.status) throw new Error(d.description || d.message || 'Error');
        return d;
      });
    }).then(function (d) {
      if (cart && typeof cart.renderContents === 'function' && d.sections) {
        if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
        cart.classList.remove('is-empty'); // as the theme's product-form.js does: an empty drawer can't trap focus (QA 2026-10-13)
        cart.renderContents(d);
        document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: d } }));
      } else {
        window.location.href = '/cart';
      }
      return d;
    });
  }
  // ── end of the copied cart code (plus the is-empty line) ──

  // The header count and the drawer after the configurator tab may have changed the cart (Section Rendering API, no
  // BroadcastChannel): the drawer's own sections, replaced the way its renderContents() does, without opening it; the
  // live drawer opens from the bubble without fetching again, so it would show the old cart.
  function refreshCart() {
    const cart = getCart(), secs = cart && typeof cart.getSectionsToRender === 'function' ? cart.getSectionsToRender() : [{ id: 'cart-icon-bubble' }];
    fetch(window.location.pathname + '?sections=' + secs.map(x => x.section || x.id).join(','), { credentials: 'same-origin' })
      .then(r => (r.ok ? r.json() : null))
      .then(j => {
        if (!j) return;
        secs.forEach(x => {
          const html = j[x.section || x.id]; if (!html) return;
          const src = new DOMParser().parseFromString(html, 'text/html').querySelector(x.selector || '.shopify-section');
          const dst = x.selector ? document.querySelector(x.selector) : document.getElementById(x.id);
          if (src && dst) dst.innerHTML = src.innerHTML;
        });
        document.dispatchEvent(new CustomEvent('cart:refresh')); // the theme's add-on display (mc-cart-addons.js) runs again
        return cart ? fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json()).then(c => { cart.classList.toggle('is-empty', !(c && c.item_count > 0)); }) : null;
      })
      .catch(() => { /* the cart catches up on the next page view */ });
  }

  function mount(root) {
    const E = window.CopingTakeoff, M = window.ModaCopingEstimate;
    if (root.__mcp || !E || !M) return;
    const cfg = M.readConfig(root.getAttribute('data-config-id') || 'moda-coping-config');
    if (!cfg || !cfg.variants || !cfg.urls || !cfg.urls.configurator) return; // the fallback stays
    root.__mcp = true;
    const id = 'mcp' + (++seq);
    const colors = (cfg.colors && cfg.colors.length ? cfg.colors : M.COLORS.map(n => ({ name: n, swatch: '#ccc' }))).filter(c => M.COLORS.includes(c.name));
    const names = colors.map(c => c.name);
    const target = cfg.target || 'moda-coping';
    const pieces = cfg.pieces || {};
    const money = c => M.formatMoney(c, cfg.money_format);
    const copy = cfg.copy || {};
    // the configurator's and the cart's words: "Straight", "Inside corner", "Outside corner" (plurals only in aria labels)
    const DEF = { straight: { title: 'Straight', plural: 'Straights', size: '24 × 14 × 1.75 in.' },
      inside: { title: 'Inside corner', plural: 'Inside corners', size: '24 × 24 × 1.75 in.', note: 'The classic pool corner' },
      outside: { title: 'Outside corner', plural: 'Outside corners', size: '24 × 24 × 1.75 in.', note: 'Where the deck steps out into the pool' } };
    const piece = k => ({ ...DEF[k], ...(pieces[k] || {}) });
    const name = k => piece(k).title;
    const lower = s => s.charAt(0).toLowerCase() + s.slice(1);
    const note = k => (k === 'straight' || !piece(k).note ? '' : lower(String(piece(k).note))); // as the configurator: corners only
    // the spec line: size, then the corner's note after a dot (a line of its own in a narrow column)
    const spec = k => esc(piece(k).size) + (note(k) ? `<span class="mcp-line__note"><span class="mcp-dot" aria-hidden="true"> · </span><span class="mcp-line__nt">${esc(note(k))}</span></span>` : '');
    const photo = (k, c) => { const im = cfg.images && cfg.images[k] && cfg.images[k][c]; return im ? im.thumb || im.three_quarter || '' : ''; };

    // ── initial values: data-color, then this tab's last entry, then ?variant= (a color), then a #t= link on this URL ──
    // hand: the customer changed a count themselves, so with no length the counts shown are an order (corners only, say)
    const S0 = { color: names.includes(root.getAttribute('data-color')) ? root.getAttribute('data-color') : names[0], ft: '', inch: '', inside: 4, outside: 0, spare: 5, sealer: false, straight: null, hand: false };
    const saved = store.get();
    if (saved && typeof saved === 'object') {
      if (names.includes(saved.color)) S0.color = saved.color;
      if (typeof saved.ft === 'string') S0.ft = saved.ft.slice(0, 12);
      if (typeof saved.inch === 'string') S0.inch = saved.inch.slice(0, 12);
      const o = count(saved.outside, MAX.outside), n = count(saved.inside, MAX.inside);
      if (o != null) { S0.outside = o; S0.inside = n != null ? n : Math.min(MAX.inside, 4 + o); } // an older entry had outside only
      if (SPARES.includes(saved.spare)) S0.spare = saved.spare;
      if (saved.sealer === true) S0.sealer = true;
      if (saved.straight != null) { const s = count(saved.straight, MAX.straight); if (s != null) S0.straight = s; }
      if (saved.hand === true) S0.hand = true;
    }
    const vId = new URLSearchParams(location.search).get('variant'); // a variant link names a color
    if (vId) M.PIECES.forEach(k => Object.entries(cfg.variants[k] || {}).forEach(([c, v]) => { if (v && String(v.id) === vId && names.includes(c)) S0.color = c; }));
    const linked = M.state.decode(location.hash);
    if (linked) {
      // read once: off the URL now, so a reload restores the customer's own edits from sessionStorage, not the link again
      try { history.replaceState(history.state, '', location.pathname + location.search); } catch (e) { /* sandboxed frame: the link stays */ }
      const t = M.state.tidy({ ...M.state.defaults(), ...linked }); // a field the link leaves out takes the default, as in v3 (5 % spares)
      S0.color = names.includes(t.color) ? t.color : S0.color;
      if (SPARES.includes(t.allowance)) S0.spare = t.allowance;
      S0.sealer = t.sealer === true;
      if (t.shape === 'lf' && t.lf.in > 0) {
        const f = Math.floor(t.lf.in / 12 + 1e-9), i = +(t.lf.in - f * 12).toFixed(2);
        S0.ft = String(f); S0.inch = i ? String(i) : '';
        S0.outside = Math.min(MAX.outside, t.lf.out);
        S0.inside = Math.min(MAX.inside, linked.lf && linked.lf.inside != null ? t.lf.inside : 4 + t.lf.out); // a link without inside: a full pool
        S0.straight = null; S0.hand = false; // a link is a plan: its straights come from the estimate
      }
    }
    let V = S0;

    // ── markup ──
    const radio = (group, v, label, checked) => `<label><input type="radio" name="${id}-${group}" value="${v}"${checked ? ' checked' : ''}><span>${label}</span></label>`;
    // a quantity field in the site's input style: − count + inside one hairline box, fine line icons (Eugene 2026-10-02:
    // keep it high end). The count is a spinbutton (Arrow Up / Down step it, Page Up / Down by 5).
    const ICON = { '-1': '<svg viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M2 6h8"/></svg>',
      '1': '<svg viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M2 6h8M6 2v8"/></svg>' };
    const qty = k => {
      const many = piece(k).plural.toLowerCase(); // "More straights"
      return `<div class="mcp-line__q"><div class="mcp-adj">
            <button type="button" data-mcp-step="${k}" data-d="-1" aria-label="Fewer ${esc(many)}" aria-controls="${id}-${k}">${ICON['-1']}</button>
            <input type="text" id="${id}-${k}" data-mcp-count="${k}" inputmode="numeric" pattern="[0-9]*" maxlength="${String(MAX[k]).length}" autocomplete="off" spellcheck="false" role="spinbutton" aria-valuemin="0" aria-valuemax="${MAX[k]}" aria-labelledby="${id}-n-${k}" aria-describedby="${id}-s-${k} ${k === 'straight' ? `${id}-x-${k}` : `${id}-cn`}">
            <button type="button" data-mcp-step="${k}" data-d="1" aria-label="More ${esc(many)}" aria-controls="${id}-${k}">${ICON['1']}</button>
          </div>
            <span class="mcp-line__p" data-mcp-price="${k}"></span>
          </div>`;
    };
    const line = k => `<li class="mcp-line" data-k="${k}">
          <img class="mcp-thumb" data-mcp-photo="${k}" alt="" width="56" height="56" decoding="async">
          <div class="mcp-line__t">
            <span class="mcp-line__n" id="${id}-n-${k}">${esc(name(k))}</span>
            <span class="mcp-line__s" id="${id}-s-${k}">${spec(k)}</span>
            ${k === 'straight' ? `<span class="mcp-line__x" id="${id}-x-${k}" data-mcp="sNote" tabindex="-1"><span data-mcp="sAuto"></span><span data-mcp="sMan" hidden>${esc(TXT.sMan)}<span class="mcp-dot" aria-hidden="true"> · </span><button type="button" class="mcp-link" data-mcp="reset"></button></span><span class="mcp-line__cap" data-mcp="sCap" hidden>${esc(TXT.capBefore)}<a data-mcp="capQuote" target="${esc(target)}">Request a quote<span class="mcp-vh"> (opens in a new tab)</span></a>${esc(TXT.capAfter)}</span></span>` : ''}
          </div>
          ${qty(k)}
        </li>`;
    // the sealer: the paver page's add-on, shown only when the config offers it
    const SEAL = M.sealer(cfg, {}).offered ? { ...cfg.addons.sealer } : null;
    const CHECK = '<svg viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M2.5 6.2l2.3 2.3 4.7-4.9"/></svg>';
    root.innerHTML = `<div class="mcp-in">
      <div class="mcp-color" role="radiogroup" aria-labelledby="${id}-cl">
        <span class="mcp-lbl" id="${id}-cl">Color: <span data-mcp="colorName">${esc(V.color)}</span></span>
        <div class="mcp-swatches">${colors.map(c => `<label class="mcp-swatch" title="${esc(c.name)}"><input type="radio" name="${id}-color" value="${esc(c.name)}"${c.name === V.color ? ' checked' : ''}><span class="mcp-swatch__dot" style="--mcp-sw:${esc(c.swatch || '#ccc')}"></span><span class="mcp-vh">${esc(c.name)}</span></label>`).join('')}</div>
      </div>
      <div class="mcp-head"><h2 class="mcp-h">Coping calculator</h2><span class="mcp-badge">${esc(piece('straight').size)} · 3 mm bumpers</span></div>
      <fieldset class="mcp-fs mcp-len" aria-describedby="${id}-echo">
        <legend class="mcp-lbl">Total length at the water's edge</legend>
        <div class="mcp-len__row">
          <label class="mcp-unit"><input type="text" inputmode="decimal" autocomplete="off" spellcheck="false" maxlength="10" data-mcp="ft" aria-label="Feet" placeholder="e.g. 96" value="${esc(V.ft)}"><span aria-hidden="true">ft</span></label>
          <label class="mcp-unit"><input type="text" inputmode="decimal" autocomplete="off" spellcheck="false" maxlength="10" data-mcp="in" aria-label="Inches" placeholder="0" value="${esc(V.inch)}"><span aria-hidden="true">in</span></label>
        </div>
        <p class="mcp-echo" id="${id}-echo" data-mcp="echo"></p>
      </fieldset>
      <section class="mcp-pieces" aria-labelledby="${id}-ph">
        <h3 class="mcp-lbl" id="${id}-ph">Your pieces</h3>
        <ul class="mcp-lines">${M.PIECES.map(line).join('')}</ul>
        <p class="mcp-cnote" id="${id}-cn" data-mcp="cnote"></p>
      </section>
      <fieldset class="mcp-fs mcp-over" aria-describedby="${id}-sc">
        <legend class="mcp-lbl">Overage</legend>
        <div class="mcp-chips" data-mcp="spare">${SPARES.map(v => radio('spare', v, v ? v + '%' : 'None', v === V.spare)).join('')}</div>
        <p class="mcp-cap" id="${id}-sc" data-mcp="overCap">${esc(TXT.overage)}</p>
      </fieldset>
      ${SEAL ? `<fieldset class="mcp-fs mcp-seal" aria-describedby="${id}-sl">
        <legend class="mcp-lbl">Sealer</legend>
        <label class="mcp-addon"><input type="checkbox" data-mcp="sealer"${V.sealer ? ' checked' : ''} aria-describedby="${id}-sl-s">
          <span class="mcp-addon__box" aria-hidden="true">${CHECK}</span>
          <span class="mcp-addon__t"><span class="mcp-addon__n">${esc(TXT.sealName)}</span><span class="mcp-addon__s" id="${id}-sl-s" data-mcp="sealS"></span></span>
          <span class="mcp-addon__p" data-mcp="sealP"></span></label>
        <p class="mcp-cap" id="${id}-sl">${esc(SEAL.note || TXT.sealNote)}</p>
      </fieldset>` : ''}
      <div class="mcp-total" data-mcp="total" hidden><span class="mcp-total__k">Estimated total</span><strong class="mcp-total__v" data-mcp="totalV"></strong><span class="mcp-total__sub" data-mcp="totalSub"></span></div>
      <p class="mcp-quote-note" data-mcp="quoteNote" hidden>${esc(TXT.quote)}</p>
      <div class="mcp-cta">
        <button type="button" class="mcp-btn mcp-btn--primary" data-mcp="add" aria-describedby="${id}-hint" disabled>Add to cart</button>
        <a class="mcp-btn mcp-btn--primary" data-mcp="quote" target="${esc(target)}" hidden>Request a quote<span class="mcp-vh"> (opens in a new tab)</span></a>
        <p class="mcp-hint" id="${id}-hint" data-mcp="hint" hidden></p>
        <p class="mcp-msg" data-mcp="msg" role="status" hidden></p>
        <p class="mcp-moto" data-mcp="moto"></p>
        <a class="mcp-btn mcp-btn--secondary" data-mcp="open" target="${esc(target)}" aria-describedby="${id}-open">Open the configurator <span class="mcp-arrow" aria-hidden="true">↗</span><span class="mcp-vh"> (opens in a new tab)</span></a>
        <p class="mcp-cap mcp-open-cap" id="${id}-open">Draw your pool · cut plan · PDF</p>
      </div>
      <p class="mcp-vh" aria-live="polite" data-mcp="live"></p>
    </div>`;

    const $ = k => root.querySelector(`[data-mcp="${k}"]`);
    const ftEl = $('ft'), inEl = $('in'), addBtn = $('add'), quoteA = $('quote'), openA = $('open'), msg = $('msg');
    const sNote = $('sNote'), resetBtn = $('reset'), sCap = $('sCap'), capA = $('capQuote'), hint = $('hint'), motoEl = $('moto');
    const countEl = {}, priceEl = {}, photoEl = {};
    M.PIECES.forEach(k => {
      countEl[k] = root.querySelector(`[data-mcp-count="${k}"]`);
      priceEl[k] = root.querySelector(`[data-mcp-price="${k}"]`);
      photoEl[k] = root.querySelector(`[data-mcp-photo="${k}"]`);
    });
    const stepBtns = Array.from(root.querySelectorAll('[data-mcp-step]'));
    // Made to order: quote mode says the lead time comes with the quote. The cart path sends no quote, so in sales mode a
    // line that promises one shortens to "Made to order"; a confirmed lead time in copy.made_to_order (decision 2, e.g.
    // "Made to order · 4–6 weeks + about 1 week to ship") shows as written in both modes.
    const motoQuote = copy.made_to_order || 'Made to order · lead time confirmed with your quote';
    const motoSales = /quote/i.test(motoQuote) ? 'Made to order' : motoQuote;
    const motoHtml = s => (/^Made to order/.test(s) ? `<b>Made to order</b>${esc(s.slice('Made to order'.length))}` : esc(s));

    let cur = null, liveT = 0, opened = false;
    let quoteUrl = M.quoteHref(cfg, []); // "Request a quote": the Contact page, this plan in its message (kept current)
    let capped = false; // the customer typed more straights than MAX: the field shows MAX and the quote line shows

    function compute() {
      const lengthIn = M.parseFtIn(V.ft, V.inch);
      const est = M.estimate({ lengthIn: Number.isNaN(lengthIn) ? 0 : lengthIn, outside: V.outside, inside: V.inside, sparePct: V.spare });
      let status = Number.isNaN(lengthIn) ? 'invalid' : est.status;
      // an estimate past what the page will put in a cart (a typo like 960 for 96 ft, or a very long pool) is a quote,
      // as a length past the engine's limit is; so every count the page can add stays within MAX.straight
      if (status === 'ok' && est.straights > MAX.straight) status = 'long';
      const ok = status === 'ok', manual = V.straight != null;
      const auto = ok ? est.straights : 0;
      const counts = { straight: manual ? V.straight : auto, inside: V.inside, outside: V.outside };
      const n = counts.straight + counts.inside + counts.outside;
      // mode: straights always count as needed (a quote-only straight puts the page in quote mode before anything is entered)
      const seal = { sealer: !!(SEAL && V.sealer) };
      const pm = M.price(cfg, V.color, { ...counts, straight: Math.max(1, counts.straight) }, seal);
      const pr = M.price(cfg, V.color, counts, seal);
      const mode = pm.mode === 'quote' || status === 'long' ? 'quote' : 'sales';
      // with no length, counts the customer set themselves (any of the three) are an order: a few corners, say
      const ready = n > 0 && (ok || (status === 'empty' && (manual || V.hand)));
      // the link: v3's S. Nothing entered → the configurator's default pool, in this color and overage; lf carries both
      // corner counts either way, so v3's By length opens with them.
      const S = M.state.defaults();
      S.color = V.color; S.allowance = V.spare; S.sealer = seal.sealer;
      S.lf = { ...S.lf, out: est.outside, inside: est.inside };
      if (est.lengthIn > 0 && status !== 'invalid') { S.shape = 'lf'; S.lf = { in: Math.round(est.lengthIn * 16) / 16, out: est.outside, inside: est.inside }; }
      return { lengthIn, est, status, ok, manual, auto, counts, n, pr, mode, ready, S, sl: M.sealer(cfg, counts) };
    }

    // the fields show the counts in use; a field being typed in keeps its text until it's left (blank or "05" is fine mid-edit)
    function syncCounts(counts) {
      M.PIECES.forEach(k => {
        const el = countEl[k];
        if (document.activeElement !== el || count(el.value, MAX[k]) !== counts[k]) el.value = String(counts[k]);
        el.setAttribute('aria-valuenow', String(counts[k]));
      });
      stepBtns.forEach(b => {
        const k = b.getAttribute('data-mcp-step'), d = +b.getAttribute('data-d');
        b.setAttribute('aria-disabled', String(d < 0 ? counts[k] <= 0 : counts[k] >= MAX[k])); // aria-disabled, not disabled: focus stays on the button
      });
    }

    function render(cause) {
      const c = cur = compute(), { est, status, ok, manual, auto, counts, n, pr, mode, ready } = c, sales = mode === 'sales';
      const mismatch = V.inside !== V.outside + 4;
      $('colorName').textContent = V.color;
      // length: the reading, or what's wrong with it
      const bad = status === 'invalid' || status === 'short' || status === 'long';
      ftEl.setAttribute('aria-invalid', String(status === 'invalid' || status === 'short')); inEl.setAttribute('aria-invalid', String(status === 'invalid' || status === 'short'));
      const echo = $('echo');
      echo.textContent = bad ? TXT[status] : est.lengthIn > 0 ? `${M.ftIn(est.lengthIn)} around the pool` : TXT.lenHint; // the label says where
      echo.setAttribute('data-tone', bad ? 'stop' : 'calm');
      // rows
      M.PIECES.forEach(k => {
        const src = photo(k, V.color);
        if (src && photoEl[k].getAttribute('src') !== src) photoEl[k].setAttribute('src', src);
        const l = pr.lines[k];
        // sales mode: the unit price, or (a piece the cart can't take, e.g. outside corners not sold online) that adding
        // one means a quote. Quote mode: no prices at all.
        priceEl[k].textContent = !sales ? '' : l.ok && l.unitCents ? `${money(l.unitCents)} each` : TXT.pieceQuote;
        priceEl[k].classList.toggle('is-quote', sales && !l.ok);
      });
      syncCounts(counts);
      // the straights' note: where the count comes from
      $('sAuto').hidden = manual; $('sMan').hidden = !manual;
      if (manual) {
        resetBtn.textContent = ok ? `Reset to ${auto}` : 'Reset';
        resetBtn.setAttribute('aria-label', ok ? `Reset straights to the estimate, ${auto}` : 'Reset straights to the estimate');
      } else {
        $('sAuto').textContent = ok ? [TXT.sAuto, est.spare ? `incl. ${est.spare} spare` : ''].filter(Boolean).join(' · ')
          : status === 'empty' ? TXT.sEmpty : status === 'long' ? TXT.sLong : TXT.sCheck;
      }
      $('cnote').textContent = mismatch ? TXT.mismatch : '';
      if (SEAL) { // "136 sq ft · $2.00 / sq ft" and "+ $272.00" once there's an order; before it, the rate; quote mode, no prices
        const sl = c.sl, sqft = ready && sl.sqft ? `${sl.sqft} sq ft` : '', rate = sl.ok ? `${money(sl.unitCents)} / sq ft` : '';
        $('sealS').textContent = !sales ? [sqft, TXT.pieceQuote].filter(Boolean).join(' · ') // the whole order is a quote
          : !sl.ok ? [sqft, TXT.sealQuote].filter(Boolean).join(' · ') // only the sealer has no price: ticking it means a quote
          : [sqft, rate].filter(Boolean).join(' · ');
        $('sealP').textContent = sales && sl.ok && sqft ? `+ ${money(sl.lineCents)}` : '';
      }
      $('overCap').textContent = manual ? TXT.overageMan : TXT.overage;
      // total and buttons
      const showTotal = ready && sales && pr.totalCents > 0;
      $('total').hidden = !showTotal;
      if (showTotal) { $('totalV').textContent = money(pr.totalCents); $('totalSub').textContent = [plural(n, 'piece', 'pieces'), pr.sealer ? `sealer ${pr.sealer.sqft} sq ft` : '', 'shipping calculated at checkout'].filter(Boolean).join(' · '); }
      $('quoteNote').hidden = sales;
      addBtn.hidden = !sales; quoteA.hidden = sales;
      addBtn.disabled = !showTotal;
      // why Add to cart is off: a length that needs fixing says so; otherwise, a length or the customer's own counts
      hint.textContent = status === 'invalid' || status === 'short' ? TXT.addCheck : TXT.addHint;
      hint.hidden = !sales || showTotal;
      const mt = sales ? motoSales : motoQuote;
      if (motoEl.getAttribute('data-t') !== mt) { motoEl.setAttribute('data-t', mt); motoEl.innerHTML = motoHtml(mt); }
      const href = M.state.link(cfg.urls.configurator, c.S);
      openA.href = href;
      // Eugene 2026-10-13: a quote is asked on the Contact page (no quote form), with the plan written into the message
      quoteUrl = M.quoteHref(cfg, [`Color: ${V.color}`,
        c.lengthIn > 0 ? `Length at the water's edge: ${M.ftIn(c.lengthIn)}` : '',
        `Pieces: ${plural(counts.straight, 'straight', 'straights')}, ${plural(counts.inside, 'inside corner', 'inside corners')}, ${plural(counts.outside, 'outside corner', 'outside corners')}`,
        `Plan: ${new URL(href, location.href).href}`]);
      quoteA.href = capA.href = quoteUrl;
      syncCap();
      store.set(V);
      root.dispatchEvent(new CustomEvent('moda-coping:change', { bubbles: true, detail: { color: V.color, estimate: est, counts, manual, mode, state: c.S, href } }));
      clearTimeout(liveT);
      liveT = setTimeout(() => {
        const st = `${plural(counts.straight, 'straight', 'straights')}${manual ? ', set by you' : ok && est.spare ? `, including ${est.spare} spare` : ''}`;
        const list = `${st}, ${plural(counts.inside, 'inside corner', 'inside corners')}, ${plural(counts.outside, 'outside corner', 'outside corners')}${pr.sealer ? `, with sealer, ${pr.sealer.sqft} square feet` : ''}.`;
        const tail = bad ? ` ${TXT[status]}` : showTotal ? ` Estimated total ${money(pr.totalCents)}.` : !sales ? ` ${TXT.quote}` : !manual && status === 'empty' ? ` ${TXT.sEmpty}.` : '';
        const cap = capped && manual ? ` ${TXT.capBefore}Request a quote${TXT.capAfter}` : '';
        $('live').textContent = list + cap + tail + (cause === 'corners' && mismatch ? ' ' + TXT.mismatch : '');
      }, 700);
    }

    // "More than 400? Request a quote…" under "Set by you" while the count shown is the cap the customer typed past
    function syncCap() { sCap.hidden = !(capped && cur && cur.manual); }
    function setMsg(text, tone, html) { msg.setAttribute('data-tone', tone || 'calm'); if (html) msg.innerHTML = html; else msg.textContent = text || ''; msg.hidden = !html && !text; }
    // over: the customer typed past MAX (the straights' quote line shows); any other count, typed or stepped, clears it
    function setCount(k, n, over) {
      n = Math.min(MAX[k], Math.max(0, n));
      if (k === 'straight') capped = !!over;
      if (k === 'straight' ? V.straight === n : n === V[k]) { syncCounts(cur.counts); syncCap(); return; }
      V = { ...V, [k]: n, hand: true }; // the customer's own count from here on
      setMsg(''); render(k === 'straight' ? 'straight' : 'corners');
    }

    root.addEventListener('input', e => {
      const t = e.target, k = t.getAttribute && t.getAttribute('data-mcp-count');
      if (t === ftEl || t === inEl) { V = { ...V, ft: ftEl.value, inch: inEl.value }; setMsg(''); render(); return; }
      if (!k) return;
      const digits = t.value.replace(/\D/g, '');
      if (digits !== t.value) t.value = digits; // digits only
      if (digits === '') return; // mid-edit: the estimate waits; leaving the field puts the count back
      const over = +digits > MAX[k];
      if (over) t.value = String(MAX[k]);
      setCount(k, +digits, over);
    });
    root.addEventListener('change', e => {
      const t = e.target;
      if (t.getAttribute && t.getAttribute('data-mcp') === 'sealer') { V = { ...V, sealer: t.checked }; setMsg(''); render(); return; }
      if (!t.name || !t.name.startsWith(id + '-')) return; // the count fields have no name: focusout tidies them
      const g = t.name.slice(id.length + 1);
      if (g === 'color' && names.includes(t.value)) V = { ...V, color: t.value };
      else if (g === 'spare' && SPARES.includes(+t.value)) V = { ...V, spare: +t.value };
      setMsg(''); render();
    });
    root.addEventListener('focusout', e => { const k = e.target.getAttribute && e.target.getAttribute('data-mcp-count'); if (k && cur) e.target.value = String(cur.counts[k]); }); // left the field: show the count in use
    root.addEventListener('keydown', e => {
      const k = e.target.getAttribute && e.target.getAttribute('data-mcp-count'); if (!k) return;
      const d = { ArrowUp: 1, ArrowDown: -1, PageUp: 5, PageDown: -5 }[e.key]; if (!d) return;
      e.preventDefault(); setCount(k, cur.counts[k] + d); e.target.value = String(cur.counts[k]);
    });
    stepBtns.forEach(b => b.addEventListener('click', () => {
      if (b.getAttribute('aria-disabled') === 'true') return;
      const k = b.getAttribute('data-mcp-step'); setCount(k, cur.counts[k] + +b.getAttribute('data-d'));
    }));
    resetBtn.addEventListener('click', () => {
      V = { ...V, straight: null, hand: false }; capped = false; setMsg(''); render('straight');
      sNote.focus(); // the button goes away with the reset: focus stays in the row
    });
    [openA, quoteA, capA].forEach(a => a.addEventListener('click', () => { opened = true; })); // the href is already current: render() rewrites it on every input
    const onShow = () => { // a calculator the theme editor replaced has nothing to refresh: its listener goes with it
      if (!root.isConnected) { document.removeEventListener('visibilitychange', onShow); return; }
      if (document.visibilityState === 'visible' && opened) refreshCart();
    };
    document.addEventListener('visibilitychange', onShow);

    addBtn.addEventListener('click', () => {
      const c = cur && cur.mode === 'sales' && cur.ready ? cur : null; if (!c || addBtn.getAttribute('aria-busy') === 'true') return;
      const group = 'pc-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 6);
      const plan = new URL(M.state.link(cfg.urls.configurator, c.S), location.href).href;
      const props = { ...(c.ok ? { 'Pool opening': M.opening(c.est.lengthIn) } : {}), 'Corners': M.corners(c.counts.inside, c.counts.outside), _plan: plan, _v: '4' };
      const sl = c.pr.sealer; // chosen, priced (sales mode) and with something to seal
      // each piece line, then (sealed) its own sealer line in its own _group: the paver page's add-on convention, one
      // product line per group, as the live mc-cart-addons.js expects ("Applies to" product — variant (n pcs), "Coverage")
      const items = [];
      M.PIECES.filter(k => c.counts[k] > 0).forEach(k => {
        const g = `${group}-${k}`;
        items.push({ id: Number(c.pr.lines[k].id), quantity: c.counts[k], properties: sl ? { ...props, _group: g, 'Add-ons': 'Penetrating sealer' } : props });
        if (sl && sl.byPiece[k] > 0) items.push({ id: Number(sl.id), quantity: sl.byPiece[k], parent_id: Number(c.pr.lines[k].id), properties: { _group: g, 'Applies to': `${(cfg.product && cfg.product.title) || 'Pool coping'} \u2014 ${name(k)} / ${V.color} (${c.counts[k]} ${c.counts[k] === 1 ? "pc" : "pcs"})`, Coverage: `${sl.byPiece[k]} sq ft`, _v: '4' } });
      });
      const label = addBtn.textContent;
      addBtn.setAttribute('aria-busy', 'true'); addBtn.textContent = 'Adding…'; setMsg('');
      // the sealer lines are nested cart lines (parent_id: their piece's variant, same request), so the cart, checkout,
      // emails and the admin order show each under its piece; a store that refuses nesting gets the same lines flat
      const flat = () => items.map(({ parent_id, ...it }) => it);
      addItems(items).catch(err => (sl && /parent/i.test(err && err.message || '') ? addItems(flat()) : Promise.reject(err)))
        .then(() => { setMsg(TXT.added); })
        .catch(err => {
          const quote = esc(quoteUrl);
          setMsg('', 'error', `${esc(err && err.message && err.message !== 'Error' ? err.message : TXT.addFail)} <a href="${quote}" target="${esc(target)}">Request a quote<span class="mcp-vh"> (opens in a new tab)</span></a> instead.`);
        })
        .then(() => { addBtn.removeAttribute('aria-busy'); addBtn.textContent = label; });
    });

    render();

    // The product gallery follows the calculator's color (2026-10-15). Each color's Straight variant carries its
    // "Gallery images" (custom.gallery_images), so the main section re-rendered for that variant shows the lead image
    // plus that color's photos; the swap is the one global.js makes on a variant change. The page itself was rendered
    // for ?variant= or, without one, the first variant (Straight / White). The page opens on the lead image (the
    // lifestyle hero, which is the same in every color); when the customer picks a color, the gallery moves to that
    // color's first photo, so the big image changes on a phone too (Eugene 2026-10-15: "it doesn't switch on mobile").
    const mediaEl = document.querySelector('[data-section^="product-media-"]');
    if (mediaEl && cfg.variants.straight) {
      const sectionId = mediaEl.getAttribute('data-section').replace('product-media-', '');
      let shown = names[0], ask = 0;
      if (vId) M.PIECES.forEach(k => Object.entries(cfg.variants[k] || {}).forEach(([c, v]) => { if (v && String(v.id) === vId && names.includes(c)) shown = c; }));
      const sync = (color, jump) => {
        const v = cfg.variants.straight[color];
        if (color === shown || !v || !v.id) return;
        const n = ++ask;
        fetch(`${location.pathname}?variant=${encodeURIComponent(v.id)}&section_id=${encodeURIComponent(sectionId)}`)
          .then(r => (r.ok ? r.text() : Promise.reject(new Error(r.status))))
          .then(text => {
            if (n !== ask) return; // a later color won
            const html = new DOMParser().parseFromString(text, 'text/html');
            const src = html.querySelector(`[data-section="product-media-${sectionId}"]`);
            const dst = document.querySelector(`[data-section="product-media-${sectionId}"]`);
            if (!src || !dst) return;
            dst.innerHTML = src.innerHTML;
            const ms = html.querySelector(`#ProductModal-${sectionId} .product-media-modal__content`);
            const md = document.querySelector(`#ProductModal-${sectionId} .product-media-modal__content`);
            if (ms && md) md.innerHTML = ms.innerHTML;
            // global.js declares the sliders with const at the top level: shared by name, but not on window
            const section = document.getElementById(`shopify-section-${sectionId}`);
            if (section && typeof initProductPage === 'function') initProductPage(section); // eslint-disable-line no-undef
            if (document.querySelector('.js-media-list')) {
              if (typeof subSliderInit === 'function') subSliderInit(true); // eslint-disable-line no-undef
              if (typeof sliderInit === 'function') sliderInit(true); // eslint-disable-line no-undef
            }
            shown = color;
            if (jump) {
              const ml = dst.querySelector('.js-media-list');
              setTimeout(() => { if (ml && ml.swiper && ml.swiper.slides.length > 1) ml.swiper.slideTo(1, 0); }, 350);
            }
          })
          .catch(() => { /* the gallery stays as it is */ });
      };
      root.addEventListener('moda-coping:change', e => sync(e.detail && e.detail.color, true));
      sync(V.color, false); // a color restored from this tab or a link: the lead image stays first
    }
  }

  function init() { document.querySelectorAll('[data-moda-coping-calc]').forEach(mount); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init); // the theme editor re-renders sections
  window.ModaCopingPdp = { init };
})();
